import { z } from 'zod';
import { AttachmentSchema } from './attachment.js';
import { ChannelKindSchema } from './channel-kind.js';
import { SenderIdentitySchema } from './sender.js';
/**
 * The wire-format a ChannelAdapter must produce after it normalizes an inbound
 * payload. Core pipeline consumes only this shape; adapter-specific structures
 * are opaque below this boundary.
 *
 * Mirrors §9 of docs/architecture/channels.md.
 */
export const NormalizedInboundMessageSchema = z.object({
    schemaVersion: z.literal(1),
    messageId: z.guid(),
    channel: z.object({
        type: ChannelKindSchema,
        accountId: z.string().min(1),
        nativeMessageId: z.string().min(1),
    }),
    thread: z.object({
        nativeThreadId: z.string().nullable(),
        conversationKey: z.string().min(1),
        inReplyToNativeId: z.string().nullable(),
        /**
         * RFC 5322 `References` chain (oldest → newest). Used by the Threading v2
         * resolver to collapse conversations whose provider `threadId` drifted mid
         * chain. Adapters that have no notion of references should pass `[]`.
         */
        externalReferences: z.array(z.string()).default([]),
    }),
    sender: SenderIdentitySchema,
    recipients: z.object({
        to: z.array(SenderIdentitySchema),
        cc: z.array(SenderIdentitySchema).default([]),
        bcc: z.array(SenderIdentitySchema).default([]),
    }),
    content: z.object({
        subject: z.string().nullable(),
        text: z.string().nullable(),
        html: z.string().nullable(),
        language: z.string().nullable(),
        attachments: z.array(AttachmentSchema).default([]),
        headers: z.record(z.string(), z.string()).default({}),
    }),
    timestamps: z.object({
        sentAt: z.string().datetime({ offset: true }).nullable(),
        receivedAt: z.string().datetime({ offset: true }),
        ingestedAt: z.string().datetime({ offset: true }),
    }),
    rawPayload: z.object({
        blobRef: z.string().min(1),
        contentHashSha256: z.string().regex(/^[a-f0-9]{64}$/, 'must be lowercase hex sha256'),
        sizeBytes: z.number().int().nonnegative(),
    }),
});
//# sourceMappingURL=normalized-inbound-message.js.map